"use client";

import Button from "@/components/ui/button/Button";
import InlineCreateModal from "@/components/ui/InlineCreateModal";
import SearchableSelect from "@/components/ui/SearchableSelect";
import Input from "@/components/form/input/InputField";
import Label from "@/components/form/Label";
import { Modal } from "@/components/ui/modal/index";
import { rmcClient } from "@/lib/rmcClient";
import { formatInr, formatRoundOffInr, roundMoney, roundOffToWholeRupee } from "@/lib/utils";
import { gstSchema } from "@/lib/validation";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useRef, useState } from "react";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import toast from "react-hot-toast";
import { z } from "zod";
import { useRateLookup } from "@/hooks/useRateLookup";
import ChallanPrint from "@/components/print/ChallanPrint";
import { useRouter } from "next/navigation";

const itemSchema = z.object({
  grade_id: z.coerce
    .number()
    .refine((n) => Number.isFinite(n) && n > 0, "Grade required"),
  qty_cum: z.coerce.number().positive(),
  rate: z.coerce.number().positive(),
  remark: z.string().optional(),
});

const schema = z.object({
  date: z.string().min(1),
  vehicle_id: z.coerce
    .number()
    .refine((n) => Number.isFinite(n) && n > 0, "Vehicle required"),
  driver_id: z.coerce.number().refine((n) => Number.isFinite(n) && n > 0, "Driver required"),
  party_id: z.coerce
    .number()
    .refine((n) => Number.isFinite(n) && n > 0, "Party required"),
  site_id: z.coerce
    .number()
    .refine((n) => Number.isFinite(n) && n > 0, "Site required"),
  order_by: z.string().optional(),
  batch_no: z.string().optional(),
  items: z.array(itemSchema).min(1, "At least one grade row required"),
});

export default function SalesChallanForm({ challanId = null }) {
  const router = useRouter();
  const qc = useQueryClient();
  const { fetchRateDetails } = useRateLookup();
  const printRef = useRef(null);
  const [lastChallan, setLastChallan] = useState(null);
  const [partyModal, setPartyModal] = useState(false);
  const [siteModal, setSiteModal] = useState(false);
  const [newParty, setNewParty] = useState({ name: "", gst_number: "", address: "" });
  const [newSiteName, setNewSiteName] = useState("");
  const [taxType, setTaxType] = useState("NON_GST");
  const [gstRate, setGstRate] = useState(0);
  const [invoicePrompt, setInvoicePrompt] = useState(false);
  const [createdChallanId, setCreatedChallanId] = useState(null);

  const { data: vehicles = [] } = useQuery({
    queryKey: ["vehicles"],
    queryFn: async () => (await rmcClient.listVehicles()).data,
  });
  const { data: drivers = [] } = useQuery({
    queryKey: ["drivers", "salesChallanForm"],
    queryFn: async () => (await rmcClient.listDrivers()).data,
  });
  const { data: parties = [] } = useQuery({
    queryKey: ["salesParties"],
    queryFn: async () => (await rmcClient.listSalesParties()).data,
  });
  const { data: challanNumberData } = useQuery({
    queryKey: ["nextSalesChallanNumber"],
    queryFn: async () => (await rmcClient.getSalesChallanNumber()).data,
    enabled: !challanId,
  });
  const { data: existingChallan, isLoading: isLoadingChallan } = useQuery({
    queryKey: ["salesChallan", challanId],
    queryFn: async () => (await rmcClient.getSalesChallan(challanId)).data,
    enabled: !!challanId,
  });

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      date: new Date().toISOString().slice(0, 10),
      driver_id: "",
      order_by: "",
      batch_no: "",
      items: [{ grade_id: "", qty_cum: "", rate: "", remark: "" }],
    },
  });

  const { fields, append, remove, replace } = useFieldArray({ control, name: "items" });
  const partyId = watch("party_id");
  const items = watch("items");
  const challanDate = watch("date");
  const { data: grades = [], isLoading: isLoadingGrades } = useQuery({
    queryKey: ["grades", partyId],
    queryFn: async () => (await rmcClient.listGrades({ party_id: partyId })).data,
    enabled: !!partyId,
  });
  const gradeIdSignature = (items || [])
    .map((it) => (it?.grade_id ? String(it.grade_id) : ""))
    .join("|");
  const selectedGradeIds = (items || [])
    .map((it) => Number(it?.grade_id))
    .filter((id) => Number.isFinite(id) && id > 0);

  const party = parties.find((p) => p.id === partyId);
  const sites = party?.sites || [];
  const dateValue = watch("date");
  const resolveGradeId = React.useCallback(
    (item) => {
      const directId = item?.grade_id ?? item?.grade?.id;
      if (Number.isFinite(Number(directId)) && Number(directId) > 0) return Number(directId);
      const fallbackName = String(item?.grade_name || item?.grade?.name || "")
        .trim()
        .toLowerCase();
      if (!fallbackName) return "";
      const matched = (grades || []).find(
        (g) => String(g?.name || "").trim().toLowerCase() === fallbackName
      );
      return matched?.id ?? "";
    },
    [grades]
  );

  useEffect(() => {
    if (dateValue) return;
    setValue("date", new Date().toISOString().slice(0, 10), {
      shouldDirty: false,
      shouldValidate: true,
    });
  }, [dateValue, setValue]);

  useEffect(() => {
    if (!challanId || !existingChallan) return;
    setTaxType(existingChallan.tax_type === "GST" ? "GST" : "NON_GST");
    setGstRate(Number(existingChallan.gst_rate || 0));
    const dn = String(existingChallan.driver_name || "").trim();
    const driverMatch = dn
      ? drivers.find((d) => String(d.name || "").trim().toLowerCase() === dn.toLowerCase())
      : null;
    reset({
      vehicle_id: existingChallan.vehicle_id ?? "",
      driver_id: driverMatch?.id ?? "",
      date: existingChallan.date || new Date().toISOString().slice(0, 10),
      party_id: existingChallan.party_id ?? "",
      site_id: existingChallan.site_id ?? "",
      order_by: existingChallan.order_by || "",
      batch_no: existingChallan.batch_no || "",
      items:
        Array.isArray(existingChallan.items) && existingChallan.items.length
          ? existingChallan.items.map((it) => ({
              grade_id: resolveGradeId(it),
              qty_cum: it.qty_cum ?? it.qty ?? "",
              rate: it.rate ?? "",
              remark: it.remark || "",
            }))
          : [{ grade_id: "", qty_cum: "", rate: "", remark: "" }],
    });
  }, [challanId, existingChallan, reset, resolveGradeId, drivers]);

  useEffect(() => {
    if (!partyId) {
      setTaxType("NON_GST");
      setGstRate(0);
      return;
    }
    if (!selectedGradeIds.length) {
      setTaxType("NON_GST");
      setGstRate(0);
      return;
    }
    const challanDay = String(challanDate || "").slice(0, 10);
    const chosenDay = challanDay || new Date().toISOString().slice(0, 10);
    let cancelled = false;
    (async () => {
      try {
        const rows = (
          await rmcClient.listQuotations({
            party_id: partyId,
            is_active: true,
          })
        ).data;
        const inRange = (q) => {
          const qDate = String(q?.date || "").slice(0, 10);
          const validUntil = q?.valid_until ? String(q.valid_until).slice(0, 10) : "";
          if (qDate && qDate > chosenDay) return false;
          if (validUntil && validUntil < chosenDay) return false;
          return true;
        };
        const hasAnySelectedGradeFromRow = (q) => {
          if (Array.isArray(q?.grades_covered) && q.grades_covered.length) {
            return selectedGradeIds.some((gid) => q.grades_covered.includes(gid));
          }
          const rateRows = Array.isArray(q?.grade_rates)
            ? q.grade_rates
            : Array.isArray(q?.items)
              ? q.items
              : [];
          return selectedGradeIds.some((gid) =>
            rateRows.some((r) => {
              const qGid = Number(r?.grade_id ?? r?.grade?.id);
              return qGid === gid && Number(r?.rate || 0) > 0;
            })
          );
        };

        const ordered = [...(rows || [])]
          .filter(inRange)
          .sort((a, b) => String(b?.date || "").localeCompare(String(a?.date || "")));

        let match = ordered.find(hasAnySelectedGradeFromRow);
        if (!match) {
          for (const candidate of ordered) {
            const detail = (await rmcClient.getQuotation(candidate.id)).data;
            const rateRows = Array.isArray(detail?.grade_rates)
              ? detail.grade_rates
              : Array.isArray(detail?.items)
                ? detail.items
                : [];
            const ok = selectedGradeIds.some((gid) =>
              rateRows.some((r) => {
                const qGid = Number(r?.grade_id ?? r?.grade?.id);
                return qGid === gid && Number(r?.rate || 0) > 0;
              })
            );
            if (ok) {
              match = detail;
              break;
            }
          }
        }
        if (cancelled) return;
        if (match?.tax_type === "GST") {
          setTaxType("GST");
          setGstRate(Number(match.gst_rate || 0));
        } else {
          setTaxType("NON_GST");
          setGstRate(0);
        }
      } catch {
        if (cancelled) return;
        setTaxType("NON_GST");
        setGstRate(0);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [partyId, challanDate, gradeIdSignature]);

  const saveParty = useMutation({
    mutationFn: (row) => rmcClient.createSalesParty(row),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["salesParties"] });
      setValue("party_id", res.data.id);
      setPartyModal(false);
      toast.success("Party saved");
    },
  });

  const saveSite = useMutation({
    mutationFn: ({ pid, name }) => rmcClient.addSalesSite(pid, name),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["salesParties"] });
      setValue("site_id", res.data.id);
      setSiteModal(false);
      setNewSiteName("");
      toast.success("Site added");
    },
  });

  const submit = useMutation({
    mutationFn: (data) => {
      const driver = drivers.find((d) => Number(d.id) === Number(data.driver_id));
      const { driver_id: _did, ...rest } = data;
      const payload = {
        ...rest,
        driver_name: driver?.name || "",
        order_by: typeof data.order_by === "string" ? data.order_by : "",
      };
      return challanId
        ? rmcClient.updateSalesChallan(challanId, payload)
        : rmcClient.createSalesChallan(payload);
    },
    onSuccess: (full) => {
      qc.invalidateQueries({ queryKey: ["salesChallans"] });
      qc.invalidateQueries({ queryKey: ["materials"] });
      qc.invalidateQueries({ queryKey: ["nextSalesChallanNumber"] });
      if (challanId) qc.invalidateQueries({ queryKey: ["salesChallan", challanId] });
      const w = full.stock_warnings || [];
      if (w.length) {
        toast(`Stock warning: ${w.join(" ")} Challan saved.`, {
          duration: 6000,
          icon: "⚠",
        });
      }
      if (challanId) {
        toast.success(`${full?.data?.challan_no || existingChallan?.challan_no || "Challan"} updated`);
        router.push("/sales/challans");
      } else {
        const challanNo = full?.data?.challan_no || full?.challan_no || "Challan";
        toast.success(`${challanNo} created`);
        const challanPayload = full?.data || full;
        const createdId = challanPayload?.id ?? null;
        setCreatedChallanId(createdId);
        setInvoicePrompt(Boolean(createdId));
        setLastChallan(full?.data || full);
        reset({
          date: new Date().toISOString().slice(0, 10),
          vehicle_id: "",
          driver_id: "",
          party_id: "",
          site_id: "",
          order_by: "",
          batch_no: "",
          items: [{ grade_id: "", qty_cum: "", rate: "", remark: "" }],
        });
        setTimeout(() => printRef.current?.openPrint?.(), 300);
      }
    },
    onError: () =>
      toast.error(challanId ? "Failed to update. Please try again." : "Failed to save. Please try again."),
  });

  const applyRateForRow = async (index, gradeId) => {
    if (!partyId || !gradeId) return;
    const info = await fetchRateDetails(partyId, gradeId, challanDate);
    const r = info?.rate ?? null;
    if (r != null) {
      setValue(`items.${index}.rate`, r);
    } else {
      toast("⚠ No quotation found for this grade. Enter rate manually.", { icon: "⚠" });
    }
  };

  const createInvoice = useMutation({
    mutationFn: ({ challanId, taxType }) =>
      rmcClient.createSalesInvoice({ challan_id: challanId, tax_type: taxType }),
    onSuccess: () => {
      toast.success("Invoice generated");
      setInvoicePrompt(false);
      setCreatedChallanId(null);
      qc.invalidateQueries({ queryKey: ["salesInvoices"] });
    },
    onError: () => toast.error("Failed to generate invoice"),
  });

  useEffect(() => {
    if (!partyId) return;
    (items || []).forEach((it, index) => {
      const gid = Number(it?.grade_id);
      if (Number.isFinite(gid) && gid > 0) {
        applyRateForRow(index, gid);
      }
    });
    // intentionally driven by party/date/grade ids only to avoid loops on rate updates
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partyId, challanDate, gradeIdSignature]);

  useEffect(() => {
    if (isLoadingGrades) return;
    if (!grades.length) return;
    const allowed = new Set((grades || []).map((g) => Number(g.id)));
    const current = items || [];
    if (!current.length) return;
    let changed = false;
    const next = current.map((it) => {
      const gid = Number(it?.grade_id);
      const isValidGrade = Number.isFinite(gid) && gid > 0 && allowed.has(gid);
      if (isValidGrade) return it;
      if (!it?.grade_id && String(it?.rate ?? "") === "") return it;
      changed = true;
      return {
        ...it,
        grade_id: "",
        rate: "",
      };
    });
    if (changed) replace(next);
  }, [partyId, grades, isLoadingGrades, items, replace]);

  const totalAmt = roundMoney(
    (items || []).reduce((a, i) => a + (Number(i?.qty_cum) || 0) * (Number(i?.rate) || 0), 0)
  );
  const gstAmt =
    taxType === "GST" ? roundMoney((totalAmt * (Number(gstRate) || 0)) / 100) : 0;
  const baseTotal = roundMoney(totalAmt + gstAmt);
  const { roundedTotal: salesGrandRounded, roundOff: salesRoundOff } =
    roundOffToWholeRupee(baseTotal);
  const nextChallanNo = challanNumberData?.next_challan_no || "--";
  const displayChallanNo = challanId
    ? isLoadingChallan
      ? "…"
      : existingChallan?.challan_no || "—"
    : nextChallanNo;

  if (challanId && isLoadingChallan) {
    return (
      <div className="rounded-xl border border-gray-800 bg-gray-950/40 px-6 py-12 text-center text-sm text-gray-400">
        Loading challan…
      </div>
    );
  }

  return (
    <div className="w-full space-y-6">
      <form className="w-full space-y-6" onSubmit={handleSubmit((d) => submit.mutate(d))}>
        <div className="grid gap-6 xl:grid-cols-4">
          <div className="space-y-6 xl:col-span-3">
            <div className="rounded-xl border border-gray-800 bg-gray-950/30 p-4">
              <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-400">
                Party & Vehicle Details
              </p>
              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          <div>
            <Controller
              name="vehicle_id"
              control={control}
              render={({ field }) => (
                <SearchableSelect
                  label="Vehicle"
                  value={field.value}
                  onChange={(id) => {
                    field.onChange(id);
                    setValue("driver_id", "");
                  }}
                  options={vehicles}
                  getLabel={(v) =>
                    `${v.registration_no}${v.name || v.alias ? ` (${v.name || v.alias})` : ""}`
                  }
                  getValue={(v) => v.id}
                />
              )}
            />
            {errors.vehicle_id && (
              <p className="mt-1 text-[11px] text-red-400">{errors.vehicle_id.message}</p>
            )}
          </div>
          <div>
            <Controller
              name="driver_id"
              control={control}
              render={({ field }) => (
                <SearchableSelect
                  label="Driver"
                  value={field.value}
                  onChange={field.onChange}
                  options={drivers.filter((d) => d.is_active !== false)}
                  getLabel={(d) =>
                    d.name
                      ? `${d.name}${d.contact_no ? ` · ${d.contact_no}` : ""}${d.employee_id ? ` (${d.employee_id})` : ""}`
                      : `Driver #${d.id}`
                  }
                  getValue={(d) => d.id}
                />
              )}
            />
            {errors.driver_id && (
              <p className="mt-1 text-[11px] text-red-400">{errors.driver_id.message}</p>
            )}
          </div>
          <div>
            <Label htmlFor="sdate">Date</Label>
            <Controller
              name="date"
              control={control}
              render={({ field }) => (
                <Input
                  id="sdate"
                  type="date"
                  value={field.value || ""}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  onKeyDown={(e) => {
                    if (e.key?.toLowerCase() !== "d") return;
                    e.preventDefault();
                    setValue("date", new Date().toISOString().slice(0, 10), {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                  }}
                  className="mt-1"
                />
              )}
            />
          </div>
          <div>
            <Controller
              name="party_id"
              control={control}
              render={({ field }) => (
                <SearchableSelect
                  label="Party"
                  value={field.value}
                  onChange={(id) => {
                    field.onChange(id);
                    setValue("site_id", undefined);
                  }}
                  options={parties}
                  createHint="Add New Party"
                  onPickCreate={() => setPartyModal(true)}
                  getSubLabel={(p) => p.gst_number}
                />
              )}
            />
            {errors.party_id && (
              <p className="mt-1 text-[11px] text-red-400">{errors.party_id.message}</p>
            )}
          </div>
          <div>
            <Controller
              name="site_id"
              control={control}
              render={({ field }) => (
                <SearchableSelect
                  label="Site"
                  value={field.value}
                  onChange={field.onChange}
                  options={sites}
                  getLabel={(s) => s.name}
                  getValue={(s) => s.id}
                  disabled={!partyId}
                  createHint="Add New Site"
                  onPickCreate={() => partyId && setSiteModal(true)}
                />
              )}
            />
            {errors.site_id && (
              <p className="mt-1 text-[11px] text-red-400">{errors.site_id.message}</p>
            )}
          </div>
          <div>
            <Label htmlFor="orderby">Order By</Label>
            <Controller
              name="order_by"
              control={control}
              render={({ field }) => (
                <Input
                  id="orderby"
                  placeholder="Optional"
                  value={field.value || ""}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  className="mt-1"
                />
              )}
            />
          </div>
          <div>
            <Label htmlFor="batchno">Batch No</Label>
            <Controller
              name="batch_no"
              control={control}
              render={({ field }) => (
                <Input
                  id="batchno"
                  placeholder="Optional"
                  value={field.value || ""}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  className="mt-1"
                />
              )}
            />
          </div>
              </div>
            </div>

            <div className="rounded-xl border border-gray-800 bg-gray-950/30 p-4">
              <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-400">
                Challan Items
              </p>
              {!partyId && (
                <p className="mb-3 text-[11px] text-gray-500">
                  Select a party first to load party-specific grades.
                </p>
              )}

              <div className="overflow-x-auto rounded-lg border border-gray-800">
                <table className="min-w-full text-xs">
                  <thead>
                    <tr className="border-b border-gray-800 bg-gray-950/80">
                      <th className="w-[190px] px-3 py-2 text-left text-gray-500">Grade</th>
                      <th className="w-[180px] px-3 py-2 text-left text-gray-500">QTY (CUM)</th>
                      <th className="w-[180px] px-3 py-2 text-left text-gray-500">Rate (₹)</th>
                      <th className="w-[130px] px-3 py-2 text-left text-gray-500">Amount</th>
                      <th className="min-w-[260px] px-3 py-2 text-left text-gray-500">Remark</th>
                      <th className="w-[40px] px-3 py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {fields.map((f, index) => (
                      <tr key={f.id} className="border-b border-gray-800/80">
                        <td className="px-3 py-2">
                          <SearchableSelect
                            value={watch(`items.${index}.grade_id`)}
                            onChange={(v) => {
                              const id = v ? Number(v) : undefined;
                              setValue(`items.${index}.grade_id`, id);
                              if (id) applyRateForRow(index, id);
                            }}
                            options={grades}
                            getLabel={(g) => g.name}
                            getValue={(g) => g.id}
                            placeholder="Select grade…"
                            disabled={!partyId}
                            overlayZClass="z-[100001]"
                            menuZClass="z-[100002]"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <Input
                            type="number"
                            step="0.001"
                            value={String(watch(`items.${index}.qty_cum`) ?? "")}
                            onChange={(e) => {
                              setValue(`items.${index}.qty_cum`, e.target.value);
                            }}
                          />
                        </td>
                        <td className="px-3 py-2">
                          <Input
                            type="number"
                            step="0.01"
                            value={String(watch(`items.${index}.rate`) ?? "")}
                            onChange={(e) => {
                              setValue(`items.${index}.rate`, e.target.value);
                            }}
                          />
                        </td>
                        <td className="px-3 py-2 text-white">
                          ₹{" "}
                          {roundMoney(
                            (Number(watch(`items.${index}.qty_cum`)) || 0) *
                              (Number(watch(`items.${index}.rate`)) || 0)
                          ).toFixed(2)}
                        </td>
                        <td className="px-3 py-2">
                          <Input {...register(`items.${index}.remark`)} />
                        </td>
                        <td className="px-3 py-2">
                          <button
                            type="button"
                            className="text-red-400 hover:text-red-300"
                            onClick={() => remove(index)}
                          >
                            ×
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {errors.items?.message && (
                <p className="mt-2 text-[11px] text-red-400">{errors.items.message}</p>
              )}
              {errors.items?.root && (
                <p className="mt-1 text-[11px] text-red-400">{errors.items.root.message}</p>
              )}

              <div className="mt-4">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => append({ grade_id: "", qty_cum: "", rate: "", remark: "" })}
                >
                  Add Grade
                </Button>
              </div>
            </div>
          </div>

          <aside className="space-y-4 xl:sticky xl:top-6 xl:self-start">
            <div className="rounded-xl border border-gray-800 bg-gray-950/60 px-4 py-3">
              <p className="text-[11px] uppercase tracking-wider text-gray-500">Challan No.</p>
              <Input value={displayChallanNo} disabled className="mt-2 font-semibold tracking-wide" />
            </div>
            <div className="rounded-xl border border-gray-800 bg-gray-900/50 px-4 py-4">
              <p className="text-[11px] uppercase tracking-wide text-gray-500">Calculation</p>
              <p className="mt-3 text-sm text-gray-300">
                Rows: <span className="font-semibold text-white">{fields.length}</span>
              </p>
              <p className="mt-1 text-sm text-gray-300">
                Subtotal: <span className="font-semibold text-white">₹ {formatInr(totalAmt)}</span>
              </p>
              <p className="mt-1 text-sm text-gray-300">
                Tax Type: <span className="font-semibold text-white">{taxType === "GST" ? "GST" : "Non-GST"}</span>
              </p>
              <p className="mt-1 text-sm text-gray-300">
                GST Rate:{" "}
                <span className="font-semibold text-white">
                  {taxType === "GST" ? `${Number(gstRate || 0).toFixed(2)}%` : "—"}
                </span>
              </p>
              <p className="mt-1 text-sm text-gray-300">
                GST Amount:{" "}
                <span className="font-semibold text-white">
                  ₹ {taxType === "GST" ? formatInr(gstAmt) : "0.00"}
                </span>
              </p>
              <p className="mt-1 text-sm text-gray-300">
                Round off:{" "}
                <span className="font-semibold text-white">₹ {formatRoundOffInr(salesRoundOff)}</span>
              </p>
              <p className="mt-1 text-sm text-gray-300">
                Total Amount:{" "}
                <span className="font-semibold text-white">₹ {formatInr(salesGrandRounded)}</span>
              </p>
            </div>
            <div className="rounded-xl border border-gray-800 bg-gray-950/30 p-4">
              <Button type="submit" size="md" className="w-full" disabled={submit.isPending}>
                {submit.isPending
                  ? challanId
                    ? "Updating challan…"
                    : "Creating challan…"
                  : challanId
                    ? "Update Sales Challan"
                    : "Save Challan"}
              </Button>
            </div>
          </aside>
        </div>
      </form>

      <ChallanPrint ref={printRef} challan={lastChallan} />

      <InlineCreateModal title="New sales party" isOpen={partyModal} onClose={() => setPartyModal(false)}>
        <div className="space-y-3">
          <div>
            <Label>Name</Label>
            <Input
              value={newParty.name}
              onChange={(e) => setNewParty({ ...newParty, name: e.target.value })}
              className="mt-1"
            />
          </div>
          <div>
            <Label>GST</Label>
            <Input
              value={newParty.gst_number}
              onChange={(e) =>
                setNewParty({ ...newParty, gst_number: e.target.value })
              }
              className="mt-1"
            />
          </div>
          <Button
            type="button"
            size="sm"
            onClick={() => {
              const p = gstSchema.safeParse(newParty.gst_number);
              if (!p.success) {
                toast.error("Invalid GST");
                return;
              }
              saveParty.mutate(newParty);
            }}
          >
            Save
          </Button>
        </div>
      </InlineCreateModal>

      <InlineCreateModal title="New site" isOpen={siteModal} onClose={() => setSiteModal(false)}>
        <div className="space-y-3">
          <Input
            placeholder="Site name"
            value={newSiteName}
            onChange={(e) => setNewSiteName(e.target.value)}
          />
          <Button
            type="button"
            size="sm"
            onClick={() => {
              if (!newSiteName.trim()) return;
              saveSite.mutate({ pid: partyId, name: newSiteName.trim() });
            }}
          >
            Save site
          </Button>
        </div>
      </InlineCreateModal>

      <Modal
        isOpen={invoicePrompt}
        onClose={() => {
          if (!createInvoice.isPending) {
            setInvoicePrompt(false);
            setCreatedChallanId(null);
          }
        }}
        className="max-w-md w-full"
      >
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-white">Generate Invoice?</h3>
          <p className="text-sm text-gray-400">
            Challan saved successfully. Choose invoice type preference to continue.
          </p>
          <div className="flex flex-wrap justify-end gap-2 border-t border-gray-800 pt-4">
            <Button
              size="sm"
              variant="outline"
              disabled={createInvoice.isPending}
              onClick={() => {
                setInvoicePrompt(false);
                setCreatedChallanId(null);
              }}
            >
              Skip
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={createInvoice.isPending || !createdChallanId}
              onClick={() =>
                createdChallanId &&
                createInvoice.mutate({ challanId: createdChallanId, taxType: "NON_GST" })
              }
            >
              Generate Non-GST
            </Button>
            <Button
              size="sm"
              disabled={createInvoice.isPending || !createdChallanId}
              onClick={() =>
                createdChallanId &&
                createInvoice.mutate({ challanId: createdChallanId, taxType: "GST" })
              }
            >
              Generate GST
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
